import React from 'react'
import styled from 'styled-components'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { sizes } from '../utils/breakpoints'
import { getLangFromPath, withLang } from '../utils/i18nRoutes'

const ServicioCryptoAgilidad = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const lang = getLangFromPath(location.pathname)
  const drivers = t('serviciosDetalle.cryptoAgilidad.drivers.items', {
    returnObjects: true,
  })
  const steps = t('serviciosDetalle.cryptoAgilidad.howWeWork.steps', {
    returnObjects: true,
  })
  const deliverables = t('serviciosDetalle.cryptoAgilidad.deliverables.items', {
    returnObjects: true,
  })
  const contactHref = withLang('/contacto', lang)

  return (
    <Container>
      <BackLink to={withLang('/servicios', lang)}>
        {t('serviciosDetalle.backLink')}
      </BackLink>

      <Hero>
        <Eyebrow>{t('serviciosDetalle.cryptoAgilidad.eyebrow')}</Eyebrow>
        <Title>{t('serviciosDetalle.cryptoAgilidad.title')}</Title>
        <Subhead>{t('serviciosDetalle.cryptoAgilidad.subhead')}</Subhead>
        <CtaRow>
          <PrimaryButton to={contactHref}>
            {t('serviciosDetalle.cryptoAgilidad.ctaPrimary')}
          </PrimaryButton>
          <SecondaryButton href="#que-incluye">
            {t('serviciosDetalle.cryptoAgilidad.ctaSecondary')}
          </SecondaryButton>
        </CtaRow>
      </Hero>

      <Section>
        <SectionHeader>
          <Kicker>{t('serviciosDetalle.cryptoAgilidad.problem.kicker')}</Kicker>
          <SectionTitle>
            {t('serviciosDetalle.cryptoAgilidad.problem.title')}
          </SectionTitle>
        </SectionHeader>
        <SectionPanel>
          <TextBlock>
            <Text>{t('serviciosDetalle.cryptoAgilidad.problem.text')}</Text>
            <Callout>
              <CalloutLabel>
                {t('serviciosDetalle.cryptoAgilidad.problem.calloutLabel')}
              </CalloutLabel>{' '}
              {t('serviciosDetalle.cryptoAgilidad.problem.calloutText')}
            </Callout>
          </TextBlock>
        </SectionPanel>
      </Section>

      <Section>
        <SectionHeader>
          <Kicker>{t('serviciosDetalle.cryptoAgilidad.drivers.kicker')}</Kicker>
          <SectionTitle>
            {t('serviciosDetalle.cryptoAgilidad.drivers.title')}
          </SectionTitle>
        </SectionHeader>
        <SectionPanel $wide>
          <CardGrid $columns={4}>
            {drivers.map((item) => (
              <DriverCard key={item.title}>
                <DriverTag>{item.tag}</DriverTag>
                <DriverTitle>{item.title}</DriverTitle>
                <DriverText>{item.text}</DriverText>
              </DriverCard>
            ))}
          </CardGrid>
        </SectionPanel>
      </Section>

      <Section id="que-incluye">
        <SectionHeader>
          <Kicker>{t('serviciosDetalle.cryptoAgilidad.howWeWork.kicker')}</Kicker>
          <SectionTitle>
            {t('serviciosDetalle.cryptoAgilidad.howWeWork.title')}
          </SectionTitle>
        </SectionHeader>
        <SectionPanel>
          <TextBlock>
            <Text>{t('serviciosDetalle.cryptoAgilidad.howWeWork.text')}</Text>
          </TextBlock>
          <Steps>
            {steps.map((step, index) => (
              <Step key={step.title}>
                <StepNumber>{String(index + 1).padStart(2, '0')}</StepNumber>
                <StepTitle>{step.title}</StepTitle>
                <StepText>{step.text}</StepText>
              </Step>
            ))}
          </Steps>
        </SectionPanel>
      </Section>

      <Section>
        <SectionHeader>
          <Kicker>{t('serviciosDetalle.cryptoAgilidad.deliverables.kicker')}</Kicker>
          <SectionTitle>
            {t('serviciosDetalle.cryptoAgilidad.deliverables.title')}
          </SectionTitle>
        </SectionHeader>
        <SectionPanel $wide>
          <CardGrid $columns={3}>
            {deliverables.map((item, index) => (
              <DeliverableCard key={item.title}>
                <DeliverableNumber>
                  {String(index + 1).padStart(2, '0')}
                </DeliverableNumber>
                <DeliverableTitle>{item.title}</DeliverableTitle>
                <DeliverableText>{item.text}</DeliverableText>
              </DeliverableCard>
            ))}
          </CardGrid>
        </SectionPanel>
      </Section>

      <FinalCta>
        <FinalCtaTitle>
          {t('serviciosDetalle.cryptoAgilidad.finalCta.title')}
        </FinalCtaTitle>
        <FinalCtaText>
          {t('serviciosDetalle.cryptoAgilidad.finalCta.text')}
        </FinalCtaText>
        <PrimaryButton to={contactHref}>
          {t('serviciosDetalle.cryptoAgilidad.finalCta.button')}
        </PrimaryButton>
      </FinalCta>
    </Container>
  )
}

export default ServicioCryptoAgilidad

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 40px;
  box-sizing: border-box;
  max-width: 980px;
  margin: 0 auto;

  @media (max-width: ${sizes.mobile}px) {
    padding: 0 20px;
  }
`

const BackLink = styled(Link)`
  align-self: flex-start;
  font-family: ${({ theme }) => theme.fonts.accentBold};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;
  margin-bottom: 24px;

  &:hover {
    color: ${({ theme }) => theme.colors.text.accent};
  }
`

const Hero = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 16px;
`

const Eyebrow = styled.span`
  display: inline-block;
  font-family: ${({ theme }) => theme.fonts.accentBold};
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.brand.dark};
  background-color: ${({ theme }) => theme.colors.brand.green};
  padding: 5px 12px;
  border-radius: 999px;
  margin-bottom: 16px;
`

const Title = styled.h1`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: clamp(26px, 3.6vw, 36px);
  line-height: 1.25;
  color: ${({ theme }) => theme.colors.text.primary};
  max-width: 720px;
  margin: 0 0 14px;
`

const Subhead = styled.p`
  font-family: ${({ theme }) => theme.fonts.accentRegular};
  font-size: 17px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 680px;
  margin: 0 0 28px;

  @media (max-width: ${sizes.mobile}px) {
    font-size: 14px;
  }
`

const CtaRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 14px;
  flex-wrap: wrap;

  @media (max-width: ${sizes.mobile}px) {
    flex-direction: column;
  }
`

const PrimaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: ${({ theme }) => theme.fonts.accentBold};
  font-size: 15px;
  color: ${({ theme }) => theme.colors.brand.dark};
  background-color: ${({ theme }) => theme.colors.brand.green};
  padding: 13px 26px;
  border-radius: 999px;
  text-decoration: none;
  transition: background-color 0.15s ease, transform 0.15s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.brand.dark};
    color: ${({ theme }) => theme.colors.text.onDark};
    transform: translateY(-2px);
  }
`

const SecondaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: ${({ theme }) => theme.fonts.accentBold};
  font-size: 15px;
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.input};
  padding: 12px 26px;
  border-radius: 999px;
  text-decoration: none;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.text.accent};
  }
`

const Section = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 0;

  &:first-of-type {
    padding-top: 0;
  }
`

const SectionHeader = styled.div`
  max-width: 640px;
  text-align: center;
  margin-bottom: 24px;
`

const Kicker = styled.span`
  display: block;
  font-family: ${({ theme }) => theme.fonts.accentBold};
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.accent};
  margin-bottom: 8px;
`

const SectionTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 22px;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;

  @media (max-width: ${sizes.mobile}px) {
    font-size: 18px;
  }
`

const SectionPanel = styled.div`
  width: 100%;
  max-width: ${({ $wide }) => ($wide ? '900px' : '760px')};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)'};
  border-radius: 20px;
  padding: 32px;

  @media (max-width: ${sizes.mobile}px) {
    padding: 20px;
    border-radius: 14px;
  }
`

const TextBlock = styled.div`
  width: 100%;
  max-width: 700px;
  text-align: left;
`

const Text = styled.p`
  font-family: ${({ theme }) => theme.fonts.accentRegular};
  font-size: 15px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 12px;
`

const Callout = styled.p`
  border-left: 3px solid ${({ theme }) => theme.colors.feedback.error};
  background-color: ${({ theme }) => theme.colors.background.card};
  padding: 16px 20px;
  border-radius: 0 12px 12px 0;
  font-family: ${({ theme }) => theme.fonts.accentRegular};
  font-size: 14px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 16px 0 0;
  max-width: 680px;
`

const CalloutLabel = styled.span`
  font-family: ${({ theme }) => theme.fonts.accentBold};
  color: ${({ theme }) => theme.colors.feedback.error};
`

const CardGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, 1fr);
  gap: 16px;

  @media (max-width: ${sizes.mobile}px) {
    grid-template-columns: 1fr;
  }
`

const DriverCard = styled.div`
  background-color: ${({ theme }) => theme.colors.background.card};
  box-shadow: ${({ theme }) => theme.shadows.card};
  border-radius: 14px;
  padding: 20px;
`

const DriverTag = styled.span`
  display: inline-block;
  font-family: ${({ theme }) => theme.fonts.accentBold};
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.accent};
  background-color: ${({ theme }) => theme.colors.border.subtle};
  padding: 4px 10px;
  border-radius: 6px;
  margin-bottom: 12px;
`

const DriverTitle = styled.p`
  font-family: ${({ theme }) => theme.fonts.accentBold};
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 6px;
`

const DriverText = styled.p`
  font-family: ${({ theme }) => theme.fonts.accentRegular};
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`

const Steps = styled.div`
  width: 100%;
  max-width: 700px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 28px;
  margin-top: 24px;

  @media (max-width: ${sizes.mobile}px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`

const Step = styled.div``

const StepNumber = styled.p`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.brand.green};
  background-color: ${({ theme }) => theme.colors.brand.dark};
  width: 28px;
  height: 28px;
  line-height: 28px;
  text-align: center;
  border-radius: 8px;
  margin: 0 0 12px;
`

const StepTitle = styled.p`
  font-family: ${({ theme }) => theme.fonts.accentBold};
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 6px;
`

const StepText = styled.p`
  font-family: ${({ theme }) => theme.fonts.accentRegular};
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`

const DeliverableCard = styled.div`
  background-color: ${({ theme }) => theme.colors.background.card};
  box-shadow: ${({ theme }) => theme.shadows.card};
  border-radius: 14px;
  padding: 22px;
`

const DeliverableNumber = styled.p`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.brand.green};
  background-color: ${({ theme }) => theme.colors.brand.dark};
  width: 28px;
  height: 28px;
  line-height: 28px;
  text-align: center;
  border-radius: 8px;
  margin: 0 0 14px;
`

const DeliverableTitle = styled.p`
  font-family: ${({ theme }) => theme.fonts.accentBold};
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 6px;
`

const DeliverableText = styled.p`
  font-family: ${({ theme }) => theme.fonts.accentRegular};
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`

const FinalCta = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.background.card};
  box-shadow: ${({ theme }) => theme.shadows.card};
  border-radius: 20px;
  padding: 40px;
  text-align: center;
  margin: 24px 0 56px;

  @media (max-width: ${sizes.mobile}px) {
    padding: 28px 20px;
  }
`

const FinalCtaTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 24px;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 10px;
`

const FinalCtaText = styled.p`
  font-family: ${({ theme }) => theme.fonts.accentRegular};
  font-size: 15px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 520px;
  margin: 0 auto 22px;
`
